import React from 'react';
import {useMergeRefs} from '@floating-ui/react';

import {mergeProps, renderElement} from '../../../utils';
import {CheckboxButton} from '../../Checkbox';
import {CheckDefaultIcon} from '../../../icons';

import {useRowItemContext} from '../RowItemContext';
import {
  RowItemView,
  RowItemBodyView,
  RowItemLabelView,
  RowItemTitle,
  RowItemDescription,
  RowItemCheckboxWrapper,
  RowItemTitleHitArea,
} from './RowItemBody.styles';

import {useRowRenamingProps} from '../__hooks';

import type {RowItemBodyProps, CheckmarkProps} from './_types';

/**
 * Helper function to check if the color variant is valid
 * @param colorVariant - The color variant to check
 * @returns True if the color variant is valid, false otherwise
 */
const isRadioCompliantColorVariant = (
  colorVariant?: CheckmarkProps['colorVariant']
) => {
  return (
    colorVariant === 'blue' ||
    colorVariant === 'green' ||
    colorVariant === 'purple'
  );
};

const CheckmarkComponent = ({colorVariant, ...props}: CheckmarkProps) => {
  const {checkmarkStyle} = useRowItemContext();

  if (checkmarkStyle === 'icon') {
    return (
      <CheckDefaultIcon
        colorVariant={colorVariant}
        {...props}
        aria-hidden
        shouldBypassAria
        style={{opacity: props.isChecked ? 1 : 0, transition: 'opacity 0.15s'}}
      />
    );
  }

  return (
    <CheckboxButton
      {...props}
      aria-hidden
      colorVariant={
        isRadioCompliantColorVariant(colorVariant) ? colorVariant : undefined
      }
      shouldBypassAria
    />
  );
};

const DEFAULT_TRUNCATION_TOOLTIP_PROPS = {
  _shouldNotBeTabbable: true,
} as const;

const DEFAULT_TRUNCATION_TOOLTIP_PROPS_SHOULD_HIDE = {
  shouldNotShowTooltip: true,
  _shouldNotBeTabbable: true,
} as const;

export const RowItemBody = React.forwardRef<HTMLElement, RowItemBodyProps>(
  (
    {children, description, isMultiline, truncationTooltipProps, ...rest},
    ref
  ) => {
    const {
      checkmarkStyle,
      isActive,
      isRounded,
      isSelected,
      density,
      prefix,
      itemProps,
      isDraggedItem,
      rowItemLabelRef,
      shouldAllowRenaming,
      onRenameOpenChange,
      isRenaming,
    } = useRowItemContext();

    const {
      textColorVariant: textColorVariantProp,
      colorVariant,
      verticalItemAlignment,
      shouldHighlightOnChecked = true,
    } = itemProps;
    const mergedRefs = useMergeRefs([ref, itemProps.ref]);

    const id = React.useId();
    const mergedProps = mergeProps(itemProps, rest);

    const titleRef = React.useRef<HTMLDivElement>(null);
    const titleHitAreaRef = React.useRef<HTMLDivElement>(null);

    const hitAreaCheck = React.useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        return Boolean(
          titleHitAreaRef.current?.contains(event.target as Node) ||
          titleRef.current?.contains(event.target as Node)
        );
      },
      []
    );

    const {onClick, renamingTabIndex} = useRowRenamingProps({
      onClick: mergedProps.onClick,
      hitAreaCheck,
      onRenameOpenChange,
      shouldAllowRenaming,
      isRenaming,
    });

    const hasCheckmark =
      (itemProps._isCheckbox && checkmarkStyle !== 'none') ||
      checkmarkStyle === 'icon';

    const isTopAligned =
      verticalItemAlignment === 'top' && isMultiline && Boolean(description);

    /**
     * The incoming `textColorVariant` prop takes priority over the `colorVariant` prop for text and icon colors.
     */
    const textColorVariant =
      textColorVariantProp ??
      (colorVariant === 'default' ? 'primary' : colorVariant);

    /**
     * If a `textColorVariant` is specified and is not `default`, it takes priority.
     * Otherwise, we want to have icons render as primary when selected and secondary all other times.
     */
    const iconColor =
      textColorVariant && textColorVariant !== 'primary'
        ? textColorVariant
        : isSelected
          ? 'primary'
          : 'secondary';

    const labelTruncationOptions = React.useMemo(
      () =>
        truncationTooltipProps?.label?.shouldNotShowTooltip
          ? DEFAULT_TRUNCATION_TOOLTIP_PROPS_SHOULD_HIDE
          : DEFAULT_TRUNCATION_TOOLTIP_PROPS,
      [truncationTooltipProps?.label?.shouldNotShowTooltip]
    );

    const descriptionTruncationOptions = React.useMemo(
      () =>
        truncationTooltipProps?.description?.shouldNotShowTooltip
          ? DEFAULT_TRUNCATION_TOOLTIP_PROPS_SHOULD_HIDE
          : DEFAULT_TRUNCATION_TOOLTIP_PROPS,
      [truncationTooltipProps?.description?.shouldNotShowTooltip]
    );

    return (
      <RowItemView
        aria-selected={isSelected ?? undefined}
        colorVariant={colorVariant}
        /**
         * We are still using our `emotionStyled` isActive and hasCheckmark props to handle styling locally.
         * We are using data-attributes here to coordinate style overrides within the SearchableListItem.
         */
        data-active={isActive}
        data-controlled-by-active={isActive !== undefined}
        data-has-checkmark={hasCheckmark}
        density={density}
        hasCheckmark={hasCheckmark}
        isActive={isActive}
        isDraggedItem={isDraggedItem}
        isMultiline={isMultiline}
        isRounded={isRounded}
        {...mergedProps}
        {...renamingTabIndex}
        onClick={onClick}
        ref={mergedRefs}
        shouldHighlightOnChecked={shouldHighlightOnChecked}
      >
        {hasCheckmark ? (
          <RowItemCheckboxWrapper isTopAligned={isTopAligned}>
            <CheckmarkComponent
              colorVariant={
                iconColor === 'primary' || iconColor === 'secondary'
                  ? 'default'
                  : iconColor
              }
              isChecked={itemProps?.isChecked || isSelected}
              isDisabled={itemProps.isDisabled}
              isError={itemProps?.isError}
              isIndeterminate={itemProps?.isIndeterminate}
            />
          </RowItemCheckboxWrapper>
        ) : null}
        <RowItemBodyView
          iconColorVariant={iconColor}
          isTopAligned={isTopAligned}
        >
          {prefix
            ? renderElement(prefix, {
                'data-row-prefix': true,
              })
            : null}
          <RowItemLabelView isMultiline={isMultiline} ref={rowItemLabelRef}>
            <RowItemTitle
              colorVariant={textColorVariant}
              font="body"
              id={id}
              isRenaming={isRenaming}
              ref={titleRef}
              truncateToLines={1}
              truncationTooltipProps={labelTruncationOptions}
            >
              {children}
            </RowItemTitle>

            {description ? (
              <RowItemDescription
                /* These are supposed to always be secondary according to design spec */
                colorVariant="secondary"
                font="body"
                /** used to shift the description down to accommodate renaming input space  */
                isMultiline={Boolean(children) && isMultiline}
                isRenaming={isRenaming}
                truncateToLines={1}
                truncationTooltipProps={descriptionTruncationOptions}
              >
                {description}
              </RowItemDescription>
            ) : null}

            {/*
             * If renaming is allowed, we render a minimal hit area for the title to be double-clickable.
             * This solves for a problem where the title is too small (i.e. below 20x20px) to be accessible.
             */}
            {shouldAllowRenaming ? (
              <RowItemTitleHitArea ref={titleHitAreaRef} />
            ) : null}
          </RowItemLabelView>
        </RowItemBodyView>
      </RowItemView>
    );
  }
);

RowItemBody.displayName = 'RowItemBody';
RowItemBody.toString = RowItemView.toString;
